import type { ShotInput, SportsGameSettings } from '../../sports.types';
import type {
  ISportDefinition,
  SportAssets,
  SportPhysicsParams,
  StartingPosition,
  ScoringZone,
  ShotEvaluation,
} from '../sport-definition.interface';
import { evaluateGoalShot } from '../zone-scoring.utils';

// ─── Physics constants ─────────────────────────────────────────────────────────

const PHYSICS: SportPhysicsParams = {
  ballRadius: 0.11,             // FIFA ball ≈ 22 cm diameter
  launchSpeedScale: 5.0,
  arcHeightFactor: 0.20,        // Low, flat arc — penalty kick stays fast and below ~1m peak
  assistCorrectionStrength: 0.55,
  goalToleranceRadius: 0.60,    // Net half-width in scene units
  optimalElevationDeg: 30,
  shankBaseThreshold: 1.5,      // Only extreme over-swings cause a shank
  shankDriftScale: 2.0,
};

/** Height of the goal opening (FIFA full-size: 2.44 m scaled to ~0.8 scene units). */
const GOAL_HEIGHT = 0.8;

// ─── Scoring zones ─────────────────────────────────────────────────────────────

/**
 * Zones defined relative to goal center.
 * X: -0.6 (left post) → +0.6 (right post)
 * Y: 0 (crossbar base) → 0.8 (top of net)
 */
const SCORING_ZONES: ScoringZone[] = [
  {
    id: 'top-left',
    points: 2,
    bounds: { xMin: -0.60, xMax: -0.15, yMin: 0.40, yMax: 0.80 },
  },
  {
    id: 'top-right',
    points: 2,
    bounds: { xMin: 0.15, xMax: 0.60, yMin: 0.40, yMax: 0.80 },
  },
  {
    id: 'center',
    points: 1,
    bounds: { xMin: -0.15, xMax: 0.15, yMin: 0.00, yMax: 0.80 },
  },
  {
    id: 'low-side',
    points: 1,
    bounds: { xMin: -0.60, xMax: -0.15, yMin: 0.00, yMax: 0.40 },
  },
  {
    id: 'low-side-right',
    points: 1,
    bounds: { xMin: 0.15, xMax: 0.60, yMin: 0.00, yMax: 0.40 },
  },
];

// ─── Starting positions ────────────────────────────────────────────────────────

const STARTING_POSITIONS: StartingPosition[] = [
  { lateralOffset:  0.0, distance: 4.5 }, // centre — classic penalty spot feel
  { lateralOffset: +0.6, distance: 4.5 }, // slight right angle
  { lateralOffset: -0.6, distance: 4.5 }, // slight left angle
  { lateralOffset: +0.4, distance: 5.0 }, // right, further back
  { lateralOffset: -0.4, distance: 5.0 }, // left, further back
];

// ─── Assets ────────────────────────────────────────────────────────────────────

const ASSETS: SportAssets = {
  // Phase 3+: ballModelPath: 'assets/sports/soccer/soccer-ball.glb'
  ballColor: '#ffffff',
};

// ─── Sport definition ──────────────────────────────────────────────────────────

export class SoccerSportDefinition implements ISportDefinition {
  readonly id = 'soccer' as const;
  readonly displayName = 'Penalty Kick';
  readonly scoringModel = 'zone' as const;
  readonly assets = ASSETS;
  readonly physicsParams = PHYSICS;
  readonly startingPositions = STARTING_POSITIONS;
  readonly scoringZones = SCORING_ZONES;
  readonly maxAttempts = 5;

  readonly arCameraHint = { pitch: -5, yaw: 0 };
  readonly arSceneRotation: [number, number, number] = [-Math.PI / 2, 0, 0];

  evaluateShot(
    input: ShotInput,
    assist: number,
    settings: SportsGameSettings
  ): ShotEvaluation {
    const {
      goalToleranceRadius,
      assistCorrectionStrength,
      shankBaseThreshold = 1.5,
      shankDriftScale = 2.0,
    } = this.physicsParams;

    // ── Whiff check ──────────────────────────────────────────────────────────
    if (input.overSwing) {
      return evaluateGoalShot({
        finalX: 0, finalY: 0, finalZ: 0,
        goalToleranceRadius, goalHeight: GOAL_HEIGHT,
        zones: SCORING_ZONES, pointsPerSuccess: settings.pointsPerSuccess,
        isWhiff: true,
      });
    }

    // ── Lateral aim: swipe x maps directly to horizontal goal position ────────
    // direction.x ∈ [-1, 1] → finalX ∈ [-goalWidth*0.8, goalWidth*0.8]
    // Shots beyond ±goalToleranceRadius miss the post.
    const correctedLateral = input.direction.x * (1 - assist * assistCorrectionStrength);
    const shankDrift =
      input.power > shankBaseThreshold
        ? (input.power - shankBaseThreshold) * shankDriftScale * (Math.random() > 0.5 ? 1 : -1)
        : 0;
    const finalX = correctedLateral * goalToleranceRadius * 1.6 + shankDrift;

    // ── Vertical: swipe y maps directly to goal height ────────────────────────
    // direction.y ∈ [0, 1] → finalY ∈ [0, GOAL_HEIGHT]
    const finalY = Math.min(GOAL_HEIGHT, Math.max(0, input.direction.y * GOAL_HEIGHT));

    return evaluateGoalShot({
      finalX,
      finalY,
      finalZ: -4.5,  // overridden by scene's currentAttemptDistance for goal sports
      goalToleranceRadius,
      goalHeight: GOAL_HEIGHT,
      zones: SCORING_ZONES,
      pointsPerSuccess: settings.pointsPerSuccess,
      isWhiff: false,
    });
  }
}
